// @flow
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useShowAlert } from 'hooks';
import API from 'services/API';
import strings from 'strings';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';

import { useStyles } from './styles';

type LinkEntitiesDrawerFormProps = {
  additionalSearchColumns: string[],
  keyToSelectAsPlaceholder: string,
  displayTemplate: (item: any) => any,
  externalLink: (item: any) => any,
  idFieldName: string,
  onClosed: () => void,
  onCompleted: (item: any) => void,
  placeholder: string,
  endpointToSaveEntity: string,
  sourceEntity: any,
  title: string,
  profileName: string
};

const LinkEntitiesDrawerForm = ({
  additionalSearchColumns,
  displayTemplate,

  externalLink,
  idFieldName,
  keyToSelectAsPlaceholder,
  onClosed,
  onCompleted,
  placeholder,
  profileName,
  sourceEntity,
  title,
  endpointToSaveEntity
}: LinkEntitiesDrawerFormProps) => {
  const classes = useStyles();
  const [selectedItem, setSelectedItem] = useState();
  const { showAlert } = useShowAlert();

  const form = useForm();
  const { register, errors, setValue } = form;
  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false
  });

  useEffect(() => {
    register({ name: idFieldName }, { required: `Please select a ${sourceEntity.singular}` });
  }, [register, sourceEntity, idFieldName]);

  const handleItemSelect = (name?: string, value: any) => {
    setSelectedItem(value);
    setValue(name, value ? value.id : value, true);
  };

  const getOptionSelected = (option, value) => option && value && option.id === value.id;

  const onSubmit = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      const response = await API.post(endpointToSaveEntity, formData);
      if (response.data && response.status === 201) {
        showAlert({
          severity: 'success',
          title: strings.formatString(strings.inventoryProfiles.common.linkEntitiesTitle, {
            entity: sourceEntity.singular
          }),
          body: strings.formatString(strings.inventoryProfiles.common.linkEntitiesDescription, {
            profileName
          })
        });

        const newItem = response.data;
        setUiState(prevState => ({
          ...prevState,
          isSaving: false,
          isSuccess: false
        }));
        onCompleted(newItem);
      }
    } catch (err) {
      showAlert({
        severity: 'error',
        title,
        body: getErrorMessage(err)
      });
      setUiState(prevState => ({
        ...prevState,
        isSaving: false,
        isSuccess: false
      }));
    } finally {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false,
        isSuccess: false
      }));
    }
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose: onClosed,
    isSaveDisabled: uiState.isSaving,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, onClose: onClosed }}
      CardHeaderProps={{ title }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: form.handleSubmit(onSubmit) }}
      onClose={onClosed}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <div>
        <AutocompleteSelect
          name={idFieldName}
          displayKey={keyToSelectAsPlaceholder}
          selectedValue={selectedItem}
          placeholder={placeholder || `Search a ${sourceEntity.singular}`}
          url={Endpoints.Search}
          onSelect={handleItemSelect}
          getOptionSelected={getOptionSelected}
          error={!!errors[idFieldName]}
          errorText={errors[idFieldName] && errors[idFieldName].message}
          typeahead
          typeaheadLimit={50}
          typeaheadParams={{
            entityType: sourceEntity.id,
            inColumns: additionalSearchColumns.join(',')
          }}
          renderOption={displayTemplate}
        />
        {selectedItem && (
          <div className={classes.details}>
            {displayTemplate(selectedItem)}
            {externalLink && <div className={classes.link}>{externalLink(selectedItem)}</div>}
          </div>
        )}
      </div>
    </FPDrawer>
  );
};

LinkEntitiesDrawerForm.defaultProps = {
  additionalSearchColumns: [],
  keyToSelectAsPlaceholder: 'title',
  externalLink: undefined
};

export default LinkEntitiesDrawerForm;
